import { Component, Input, OnInit, signal, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LucideAngularModule, Eye, Code } from 'lucide-angular';
import { Message } from '../../../../core/models/message.model';
import { WhatsAppFormatter } from '../../../../core/utils/whatsapp-formatter';
import { TraceFormatterService } from '../../../../core/services/trace-formatter.service';

@Component({
  selector: 'app-discarded-message-inline',
  standalone: true,
  imports: [
    CommonModule,
    DatePipe,
    MatButtonModule,
    MatExpansionModule,
    MatTooltipModule,
    LucideAngularModule
  ],
  template: `
    <div class="flex justify-start mb-3">
      <div class="flex items-start gap-3 max-w-[85%] w-full">
        <!-- Avatar -->
        <div class="w-8 h-8 bg-gradient-to-br from-orange-400 to-orange-500 dark:from-orange-500 dark:to-orange-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
          <span class="text-white text-xs font-semibold">AI</span>
        </div>
    
        <!-- Accordion Container -->
        <div class="flex-1">
          <!-- Accordion for thinking traces -->
          <details class="my-3">
            <summary class="cursor-pointer px-3 py-2 text-s font-medium text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-900/20 hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors flex items-center gap-2 rounded-lg border border-dashed border-orange-300 dark:border-orange-700/50">
              <svg class="w-4 h-4 transition-transform [[open]_&]:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
              <span class="text-[10px] font-semibold px-1.5 py-0.5 bg-orange-500 text-white rounded mr-1">DESCARTADO</span>
              <span>Pensamiento</span>
              @if (messages.length > 1) {
                <span class="text-[10px] font-semibold px-1.5 py-0.5 bg-orange-200 dark:bg-orange-800 text-orange-800 dark:text-orange-200 rounded-full ml-1">{{ messages.length }}</span>
              }
              <span class="text-xs ml-auto opacity-70">{{ formatTime(message.timestamp) }}</span>
            </summary>
    
            @if (parsedTraces && parsedTraces.length > 0) {
              <div class="p-3 space-y-2 bg-white dark:bg-dark-surface max-h-[450px] min-h-0 overflow-y-auto rounded-b-lg border-x border-b border-dashed border-orange-300 dark:border-orange-700/50">
                @for (agentGroup of traceFormatter.groupTracesByAgent(parsedTraces); track agentGroup.agentName) {
                  <!-- Encabezado del agente -->
                  <div class="text-base font-semibold pt-2">
                    {{ agentGroup.agentName }}
                  </div>
                  <div class="border-l-2 border-light-border dark:border-dark-border pl-3 ml-1">
                    <!-- Trazas del agente -->
                    <div class="space-y-3">
                      @for (trace of agentGroup.traces; track $index) {
                        <!-- Thinking Trace -->
                        @if (trace.type === 'thinking') {
                          <div class="border-l-2 rounded border-purple-300 dark:border-purple-700 bg-purple-50 dark:bg-purple-900/10 px-2">
                            <div class="flex flex-row items-center justify-between">
                              <div class="text-sm mt-1 font-medium text-purple-600 dark:text-purple-400 mb-1">Thinking</div>
                              <div class="text-sm font-normal text-light-text-tertiary dark:text-dark-text-tertiary">{{ agentGroup.timestamp | date: 'HH:mm:ss' }}</div>
                            </div>
                            <div class="content-box compact-scroll whitespace-pre-wrap text-sm text-black dark:text-white mb-2" [innerHTML]="formatWhatsAppText(trace.content)"></div>
                          </div>
                        }
    
                        <!-- Tool Use Trace -->
                        @if (trace.type === 'tool_use') {
                          <div class="border-l-2 rounded border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/10 px-2">
                            <div class="flex flex-row items-center justify-between">
                              <div class="text-sm mt-1 font-medium text-green-600 dark:text-green-400 mb-1">
                                Tool Use: <span class="font-semibold">{{ traceFormatter.getToolName(trace) }}</span>
                              </div>
                              <div class="text-sm font-normal text-light-text-tertiary dark:text-dark-text-tertiary">{{ agentGroup.timestamp | date: 'HH:mm:ss' }}</div>
                            </div>
                            <!-- Parameters Section -->
                            <div class="text-sm mb-2">
                              @if (traceFormatter.getToolParams(trace) && traceFormatter.getToolParams(trace).length > 0) {
                                <div class="space-y-1">
                                  @for (param of traceFormatter.getToolParams(trace); track $index) {
                                    <div>
                                      <strong class="text-green-700 dark:text-green-300">{{param.name}}:</strong>
                                      <span class="text-black dark:text-white ml-1">{{param.value}}</span>
                                    </div>
                                  }
                                </div>
                              } @else {
                                <div class="text-light-text-tertiary dark:text-dark-text-tertiary italic text-xs">No parameters available</div>
                              }
                            </div>
                          </div>
                        }
    
                        <!-- Tool Output Trace -->
                        @if (trace.type === 'tool_output') {
                          <div class="border-l-2 rounded border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-900/10 px-2">
                            <div class="flex flex-row items-center justify-between">
                              <div class="text-sm mt-1 font-medium text-yellow-600 dark:text-yellow-400 mb-1">Tool Output</div>
                              <div class="flex items-center gap-2">
                                @if (traceFormatter.isKnowledgeBaseResponse(trace.content) || traceFormatter.isJsonOutput(trace.content)) {
                                  <button
                                    mat-icon-button
                                    [class]="'!w-6 !h-6 !p-0 ' + (getViewMode(trace) === 'clean' ? '!bg-yellow-100 dark:!bg-yellow-900/30' : '')"
                                    (click)="setViewMode(trace, 'clean')"
                                    [matTooltip]="traceFormatter.isKnowledgeBaseResponse(trace.content) ? 'Ver artículos' : 'Ver formato limpio'"
                                    >
                                    <lucide-icon
                                      [img]="EyeIcon"
                                      class="w-4 h-4 text-yellow-600 dark:text-yellow-400"
                                    ></lucide-icon>
                                  </button>
                                }
                                <button
                                  mat-icon-button
                                  [class]="'!w-6 !h-6 !p-0 ' + (getViewMode(trace) === 'raw' ? '!bg-yellow-100 dark:!bg-yellow-900/30' : '')"
                                  (click)="setViewMode(trace, 'raw')"
                                  matTooltip="Ver respuesta completa"
                                  >
                                  <lucide-icon
                                    [img]="CodeIcon"
                                    class="w-4 h-4 text-yellow-600 dark:text-yellow-400"
                                  ></lucide-icon>
                                </button>
                                <div class="text-sm font-normal text-light-text-tertiary dark:text-dark-text-tertiary">{{ agentGroup.timestamp | date: 'HH:mm:ss' }}</div>
                              </div>
                            </div>
                            @if (getViewMode(trace) === 'clean' && traceFormatter.isKnowledgeBaseResponse(trace.content)) {
                              <div class="knowledge-articles-grid mb-2">
                                @for (article of traceFormatter.parseKnowledgeBaseResponse(trace.content); track article.order) {
                                  <div class="kb-article-card">
                                    <div class="flex flex-row pt-1.5">
                                      <span class="text-sm font-semibold">#{{ article.order }}</span>
                                      <span class="text-sm font-semibold">{{ article.mainCategory }}</span>
                                    </div>
                                    <div class="pt-0.7">
                                      <span class="text-sm font-medium">Subcategoría: </span><span class="kb-article-subcategory text-sm font-normal">{{ article.subCategory }}</span>
                                    </div>
                                    <div class="pt-0.7">
                                      <span class="text-sm font-medium">Question: </span><span class="text-sm font-normal">{{ article.question }}</span>
                                    </div>
                                    <div class="pt-0.7">
                                      <span class="text-sm font-medium">Response: </span><span class="kb-article-response text-sm font-normal">{{ article.response }}</span>
                                    </div>
                                  </div>
                                }
                              </div>
                            } @else if (getViewMode(trace) === 'clean' && traceFormatter.isJsonOutput(trace.content)) {
                              <div class="mb-2 max-w-full overflow-y-auto">
                                <pre class="text-xs font-normal whitespace-pre-wrap break-words" [innerHTML]="traceFormatter.formatJsonOutput(trace.content)"></pre>
                              </div>
                            } @else if (getViewMode(trace) === 'raw') {
                              <div class="whitespace-pre-wrap italic p-2 rounded mb-1 text-xs" [innerHTML]="formatWhatsAppText(trace.content)"></div>
                            }
                          </div>
                        }
    
                        <!-- Final Answer Trace -->
                        @if (trace.type === 'final_answer') {
                          <div class="border-l-2 rounded border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/10 px-2">
                            <div class="flex flex-row items-center justify-between">
                              <div class="text-sm mt-1 font-medium text-blue-600 dark:text-blue-400 mb-1">Final Answer</div>
                              <div class="text-sm font-normal text-light-text-tertiary dark:text-dark-text-tertiary">{{ agentGroup.timestamp | date: 'HH:mm:ss' }}</div>
                            </div>
                            <div class="content-box compact-scroll whitespace-pre-wrap text-sm text-black dark:text-white mb-2" [innerHTML]="formatWhatsAppText(trace.content)"></div>
                          </div>
                        }
    
                        <!-- Model Invocation Info Trace -->
                        @if (trace.type === 'model_invocation_info') {
                          <div class="border-l-2 rounded border-light-border dark:border-dark-border bg-light-bg-secondary dark:bg-dark-bg-secondary px-2">
                            <div class="flex flex-row items-center justify-between">
                              <div class="text-sm mt-1 font-medium text-light-text-secondary dark:text-dark-text-secondary mb-1">Métricas del modelo</div>
                              <div class="text-sm font-normal text-light-text-tertiary dark:text-dark-text-tertiary">{{ agentGroup.timestamp | date: 'HH:mm:ss' }}</div>
                            </div>
                            <div class="content-box compact-scroll whitespace-pre-wrap text-sm text-black dark:text-white mb-2" [innerHTML]="formatWhatsAppText(trace.content)"></div>
                          </div>
                        }

                        <!-- Info / Summary Trace -->
                        @if (trace.type === 'info') {
                          <div class="border-l-2 rounded border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/10 px-2">
                            <div class="flex flex-row items-center justify-between">
                              <div class="text-sm mt-1 font-medium text-gray-600 dark:text-gray-400 mb-1">Info</div>
                              <div class="text-sm font-normal text-light-text-tertiary dark:text-dark-text-tertiary">{{ agentGroup.timestamp | date: 'HH:mm:ss' }}</div>
                            </div>
                            <div class="content-box compact-scroll whitespace-pre-wrap text-xs text-black dark:text-white max-h-32 overflow-y-auto mb-2" [innerHTML]="formatWhatsAppText(trace.content)"></div>
                          </div>
                        }
                      }
                    </div>
                  </div>
                }
              </div>
            }
          </details>
    
          <!-- Discarded message bubbles — one per message that has visible text -->
          @for (msg of messages; track msg.id) {
            @if (msg.content.trim()) {
              <div class="message-bubble message-bot !border !border-dashed !border-orange-300 dark:!border-orange-700/50 mb-2">
                <div class="text-sm leading-relaxed whitespace-pre-wrap text-light-text dark:text-dark-text" [innerHTML]="formatWhatsAppText(msg.content)"></div>
    
                <div class="flex items-center justify-between mt-3">
                  <span class="text-xs text-orange-700 dark:text-orange-300 font-medium">
                    <span class="text-[10px] font-semibold px-1.5 py-0.5 bg-orange-500 text-white rounded mr-1">DESCARTADO</span>
                    @if (msg.agentName) {
                      {{ msg.agentName }}
                    }
                  </span>
                  <p class="small">
                    {{ formatTime(msg.timestamp) }}
                  </p>
                </div>
              </div>
            }
          }
        </div>
      </div>
    </div>
    `,
  styles: [`
    details summary::-webkit-details-marker {
      display: none;
    }
  `]
})
export class DiscardedMessageInlineComponent implements OnInit {
  /** All discarded messages in this group (same answeredMessageId). */
  @Input() messages: Message[] = [];

  /** Convenience getter — points to the first message in the group. */
  get message(): Message {
    return this.messages[0];
  }

  // Injected services
  public traceFormatter = inject(TraceFormatterService);
  
  // Lucide icons
  readonly EyeIcon = Eye;
  readonly CodeIcon = Code;
  
  // Track view mode for each trace
  private readonly traceViewModes = signal<Map<string, 'clean' | 'raw'>>(new Map());
  
  expanded = false;
  parsedTraces: any[] = [];

  ngOnInit() {
    this.parseAgentTraces();
  }

  parseAgentTraces() {
    this.parsedTraces = [];

    for (const msg of this.messages) {
      if (!msg.agentTrace) continue;

      try {
        const agentTrace = typeof msg.agentTrace === 'string'
          ? JSON.parse(msg.agentTrace)
          : msg.agentTrace;

        if (!Array.isArray(agentTrace)) continue;

        const msgTraces = agentTrace.flatMap((agent: any) =>
          (agent.traces ?? []).map((trace: any) => ({
            type: trace.type,
            content: this.formatTraceContent(trace),
            timestamp: this.parseTimestamp(trace.timestamp),
            agentName: agent.agent_name || agent.agentName,
            body: trace.body,
            rawTrace: { body: trace.body }
          }))
        );

        this.parsedTraces.push(...msgTraces);
      } catch (error) {
        console.error('Error parsing agentTrace:', error);
      }
    }
  }

  private parseTimestamp(ts: any): Date | null {
    if (!ts) return null;
    const date = new Date(ts);
    return isNaN(date.getTime()) ? null : date;
  }

  // Formats the thinking trace content
  private formatThinkingTrace(trace: any): string {
    return trace.body?.text || JSON.stringify(trace.body || {}, null, 2);
  }

  // Formats the tool output trace content
  private formatToolOutputTrace(trace: any): string {
    return JSON.stringify(trace.body || {}, null, 2);
  }

  formatTraceContent(trace: any): string {
    switch (trace.type) {
      case 'thinking':
        return this.formatThinkingTrace(trace);
      case 'tool_use': {
        if (!trace.body) return this.formatToolOutputTrace(trace);
        const toolName = trace.body.tool_name || 'Unknown';
        const toolInput = this.extractToolInput(trace.body);
        return this.formatToolParams(toolName, toolInput);
      }
      case 'tool_output':
        return this.formatThinkingTrace(trace);
      case 'final_answer':
        return this.formatThinkingTrace(trace);
      case 'info':
        return this.formatThinkingTrace(trace);
      case 'model_invocation_info': {
        if (!trace.body) return this.formatToolOutputTrace(trace);
        const { usage = {}, model_id } = trace.body;
        const modelId = model_id || 'Unknown';
        const totalTokens = this.getTokenValue(usage, 'total_tokens');
        const inputTokens = this.getTokenValue(usage, 'input_tokens');
        const outputTokens = this.getTokenValue(usage, 'output_tokens');
        return `Model: ${modelId}\nTokens: ${totalTokens} (input: ${inputTokens}, output: ${outputTokens})`;
      }
        
      default:
        return this.formatToolOutputTrace(trace);
    }
  }

  /** Obtiene el valor de un token de usage de forma segura */
  private getTokenValue(usage: any, key: string): number {
    return usage && typeof usage[key] === 'number' ? usage[key] : 0;
  }

  private extractToolInput(traceBody: any): any {
    if (traceBody.tool_input) {
      return traceBody.tool_input;
    }
    
    if (traceBody.params && Array.isArray(traceBody.params)) {
      return traceBody.params.reduce((acc: any, param: any) => {
        if (param.name && param.value !== undefined) {
          acc[param.name] = param.value;
        }
        return acc;
      }, {});
    }
    
    return {};
  }

  private formatToolParams(toolName: string, toolInput: any): string {
    let result = `Tool: ${toolName}`;
    for (const [key, value] of Object.entries(toolInput)) {
      result += `\n  ${key}: ${value}`;
    }
    return result;
  }

  formatWhatsAppText(text: string): string {
    return WhatsAppFormatter.format(text);
  }

  formatTime(timestamp: Date): string {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleTimeString('es-CL', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }

  /**
   * Set view mode for a trace
   */
  setViewMode(trace: any, mode: 'clean' | 'raw'): void {
    const currentMap = new Map(this.traceViewModes());
    const traceKey = this.getTraceKey(trace);
    
    // If clicking the same mode, toggle it off
    if (currentMap.get(traceKey) === mode) {
      currentMap.delete(traceKey);
    } else {
      currentMap.set(traceKey, mode);
    }
    
    this.traceViewModes.set(currentMap);
  }

  /**
   * Get view mode for a trace
   */
  getViewMode(trace: any): 'clean' | 'raw' | null {
    return this.traceViewModes().get(this.getTraceKey(trace)) || null;
  }

  /**
   * Generate a unique key for a trace
   */
  private getTraceKey(trace: any): string {
    return `${trace.type}-${trace.timestamp}`;
  }
}
